import { fmtDateTime } from '@/lib/dates.js';
/**
 * Step 2 — Informed Consent
 *
 * IN-PERSON (paper sign) flow — punch-list f21, upload-only:
 *   1. CRC fills subject identity + selects staff co-signer.
 *   2. "Start Paper Sign" → backend creates the draft consent record (no
 *      document is generated/merged — Phase 1 item 10 already retired
 *      that). A "download a blank copy" link is offered but is optional,
 *      not a required step (most sites already have a printed ICF).
 *   3. Subject signs the paper ICF; staff co-signer signs the staff section.
 *   4. CRC scans and uploads the signed PDF via presigned S3 URL.
 *   5. CRC clicks "Confirm Upload" → backend marks record signed → wizard advances.
 *
 * REMOTE flow:
 *   1. CRC fills subject identity + selects staff + enters subject email.
 *   2. "Send for e-Signing" → backend creates DocuSeal submission with two
 *      sequential signers (subject first, then staff) → sends PIN + link.
 *   3. Subject signs via email link. DocuSeal webhook marks the record.
 *   4. Staff signs via signature queue.
 *
 * ICH-GCP E6(R2) compliance:
 *   - LAR block for incapacitated adults (§4.8.12-14).
 *   - Parent co-consent for assenting minors (§4.8.15).
 *   - 21 CFR Part 11: audit trail + e-signature meaning on every record.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, Loader2, Download, Upload, CheckCircle2 } from 'lucide-react';
import { consent, studyStaff, type ConsentDocumentRow } from '@/api/client';
import { fmtDate } from '@/lib/dates';
import { humanConsentMode } from '@/lib/humanise';
import { SigningProgress, type SigningStage, type StageState } from './SigningProgress';
import {
  ConsentProcessDocumentationFields,
  EMPTY_PROCESS_DOC,
  toProcessDocPayload,
  validateProcessDoc,
  type ProcessDocDraft,
} from './ConsentProcessDocumentationFields';

function toBackendDob(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}
function normaliseToIso(raw: string): string {
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [d, m, y] = raw.split('-');
    return `${y}-${m}-${d}`;
  }
  return raw;
}
function deriveMeaning(
  role: 'subject' | 'parent' | 'assenting_minor' | 'lar',
  recordType: 'initial' | 're_consent',
): string {
  if (role === 'lar')
    return 'I am the legally authorised representative for the subject and consent on their behalf.';
  if (role === 'parent')
    return 'I am the parent/guardian of the above-named minor and give my permission for their participation.';
  if (role === 'assenting_minor')
    return 'I understand what this study involves and agree to take part.';
  if (recordType === 're_consent')
    return 'I withdraw a prior consent and grant new consent on the updated document version.';
  return 'I have read and understood the consent form and agree to participate.';
}

// PI, SI and Coordinator do the consent actions (Harish, 1 Oct 2026); admins
// have no access to patient data, so Site Admin is not offered as signer.
const CONSENT_ELIGIBLE_ROLES = new Set([
  'principal_investigator',
  'sub_investigator',
  'coordinator',
]);

export function Step2InformedConsent({
  subjectId,
  activeConsentRecordId,
  onComplete,
  triggerRef,
  saveTrigger,
  onPendingChange,
  onSavePendingChange,
  onLabelChange,
  onNextDisabledChange,
  initialEmail,
}: {
  subjectId: string;
  activeConsentRecordId?: string | null;
  /**
   * Personal email captured in the Demographics step (wizard step 2,
   * `ctms_subject_phi.email_personal`). Seeds the remote-consent email so
   * the coordinator doesn't retype an address they already entered one
   * step earlier. Null when PHI is masked for this actor's role.
   */
  initialEmail?: string | null;
  onComplete: () => void;
  onSaveAndResume?: () => void;
  triggerRef?: React.MutableRefObject<(() => void) | null>;
  saveTrigger?: React.MutableRefObject<(() => void) | null>;
  onPendingChange?: (pending: boolean) => void;
  onSavePendingChange?: (pending: boolean) => void;
  onLabelChange?: (label: string) => void;
  onNextDisabledChange?: (disabled: boolean) => void;
}) {
  const { data: ctx, isLoading: ctxLoading } = useQuery({
    queryKey: ['consent', 'wizard-context', subjectId],
    queryFn: () => consent.wizardContext(subjectId),
    enabled: !!subjectId,
  });

  const activeDoc = ctx?.active_consent_document ?? undefined;
  // Only re-consent if the flag is set AND a prior signed record exists.
  // Prevents brand-new subjects from incorrectly showing the re-consent banner
  // when the DB default or a stale flag has re_consent_required=true.
  const recordType: 'initial' | 're_consent' =
    ctx?.subject.re_consent_required && !!ctx?.subject.active_consent_record_id
      ? 're_consent'
      : 'initial';
  const studyId = ctx?.subject.study_id;

  // Study staff for co-signer dropdown
  /**
   * Approved ICF versions for the paper-upload version picker (Feedback
   * eConsent item 2). Only fetched for the paper path — the e-sign path
   * always signs the active version by construction.
   */
  const consentVersionsQ = useQuery({
    queryKey: ['consent-documents', studyId],
    queryFn: () => consent.listDocuments(studyId!),
    enabled: !!studyId,
    staleTime: 60_000,
  });
  const approvedConsentVersions = (consentVersionsQ.data?.documents ?? [])
    .filter((d) => !d.approval_status || d.approval_status === 'approved')
    .sort((a, b) => b.version_label.localeCompare(a.version_label, undefined, { numeric: true }));

  const staffQ = useQuery({
    queryKey: ['study-staff', studyId],
    queryFn: () => studyStaff.list(studyId!),
    enabled: !!studyId,
  });
  const eligibleStaff = (staffQ.data?.assignments ?? []).filter((a) =>
    CONSENT_ELIGIBLE_ROLES.has(a.role),
  );

  // ── Remote flow state (declared early — referenced in recordsQ below) ───────
  const [remoteSent, setRemoteSent] = useState(false);
  const [remoteConsentRecordId, setRemoteConsentRecordId] = useState<string | null>(null);
  // Tracks whether we've already auto-triggered the staff email for the current session
  // (so the useEffect that fires on subject_signed only sends once per session).
  const staffEmailTriggeredRef = useRef(false);
  const finalizeTriggeredRef = useRef(false);
  const qc = useQueryClient();

  // Always load consent records for this subject so we can detect webhook-completed
  // records even when the wizard's active_consent_record_id cache is still stale.
  // Also polls every 10 s after remote send until subject signature is detected.
  const recordsQ = useQuery({
    queryKey: ['consent', 'records', subjectId],
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !!subjectId,
    refetchInterval:
      remoteSent && !!remoteConsentRecordId && !activeConsentRecordId ? 10_000 : false,
  });
  const signedRecord = activeConsentRecordId
    ? recordsQ.data?.records.find((r) => r.consent_record_id === activeConsentRecordId)
    : undefined;
  const remoteRecord = remoteConsentRecordId
    ? recordsQ.data?.records.find((r) => r.consent_record_id === remoteConsentRecordId)
    : undefined;
  const subjectHasSigned = !!remoteRecord?.signed_at;

  // Poll DocuSeal submission status directly so we can detect the intermediate
  // state "subject signed, staff pending" without depending on the webhook
  // (webhooks don't reach localhost in local dev).
  const submissionStatusQ = useQuery({
    queryKey: ['consent', 'submission-status', remoteConsentRecordId],
    queryFn: () => consent.submissionStatus(remoteConsentRecordId!),
    enabled: remoteSent && !!remoteConsentRecordId && !subjectHasSigned,
    refetchInterval: remoteSent && !!remoteConsentRecordId && !subjectHasSigned ? 10_000 : false,
    staleTime: 8_000,
  });
  const docusealStatus = submissionStatusQ.data;

  // allPartiesSigned: true when DB reflects completion (webhook path) OR when
  // DocuSeal poll confirms both signed (local dev, no webhook).
  const allPartiesSigned =
    subjectHasSigned ||
    (docusealStatus?.subject_signed === true && docusealStatus?.staff_signed === true);

  // Most recent signed record — used to pre-fill the re-consent form.
  const priorRecord =
    recordType === 're_consent'
      ? (recordsQ.data?.records ?? [])
        .filter((r) => r.signed_at)
        .sort((a, b) => new Date(b.signed_at!).getTime() - new Date(a.signed_at!).getTime())[0]
      : undefined;

  // ── Form state ───────────────────────────────────────────────────────────
  const [legalName, setLegalName] = useState('');
  const [dob, setDob] = useState('');
  const [signerRole, setSignerRole] = useState<'subject' | 'parent' | 'assenting_minor' | 'lar'>(
    'subject',
  );
  // LAR details
  const [larName, setLarName] = useState('');
  const [larDob, setLarDob] = useState('');
  const [larRelationship, setLarRelationship] = useState<
    '' | 'spouse' | 'parent' | 'guardian' | 'other'
  >('');
  const [larRelationshipOther, setLarRelationshipOther] = useState('');
  // Parent/guardian details
  const [parentName, setParentName] = useState('');
  const [parentDob, setParentDob] = useState('');
  const [parentRelationship, setParentRelationship] = useState<
    '' | 'father' | 'mother' | 'guardian' | 'other'
  >('');
  const [parentRelationshipOther, setParentRelationshipOther] = useState('');
  // Staff co-signer
  const [staffId, setStaffId] = useState('');
  // Mode and remote email
  const [mode, setMode] = useState<'in_person' | 'in_person_esign' | 'remote'>('in_person');
  // Seeded from the Demographics step so remote consent doesn't ask for an
  // address the coordinator already entered. A saved draft payload still
  // wins (see the hydrate effect below) and the field stays editable.
  const [subjectEmail, setSubjectEmail] = useState(() => initialEmail ?? '');
  // "Documenting the Consent Process" sheet — required before any session.
  const [processDoc, setProcessDoc] = useState<ProcessDocDraft>(EMPTY_PROCESS_DOC);
  const [processErrors, setProcessErrors] = useState<Record<string, string>>({});
  const [hydrated, setHydrated] = useState(false);
  const [recordPrefilled, setRecordPrefilled] = useState(false);

  const [errors, setErrors] = useState<{
    legal_name?: string;
    dob?: string;
    staff?: string;
    email?: string;
    lar_name?: string;
    lar_dob?: string;
    lar_relationship?: string;
    lar_relationship_other?: string;
    parent_name?: string;
    parent_dob?: string;
    parent_relationship?: string;
    parent_relationship_other?: string;
  }>({});

  // ── In-person flow state ─────────────────────────────────────────────────
  const [generatedRecordId, setGeneratedRecordId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [signedFile, setSignedFile] = useState<File | null>(null);
  /**
   * Feedback (2026-09-29, eConsent item 2) — the ICF version the paper copy
   * was actually signed against. Paper is signed away from the screen, so it
   * is often the version that was current in clinic that day rather than the
   * one active now. Empty string = "the active version", which is what the
   * record already points at, so the BE field stays omitted in that case.
   */
  const [signedVersionId, setSignedVersionId] = useState<string>('');
  const [uploadConfirmed, setUploadConfirmed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Remote flow state ────────────────────────────────────────────────────
  // (remoteSent / remoteConsentRecordId declared above recordsQ — see top of hooks section)

  // One-shot draft hydration
  useEffect(() => {
    if (hydrated || !ctx) return;
    const payload = ctx.draft?.payload;
    if (payload) {
      if (payload.subject_full_legal_name) setLegalName(payload.subject_full_legal_name);
      if (payload.date_of_birth) setDob(normaliseToIso(payload.date_of_birth));
      if (payload.signer_role) setSignerRole(payload.signer_role as typeof signerRole);
      if (payload.mode) setMode(payload.mode as 'in_person' | 'in_person_esign' | 'remote');
      if (payload.staff_user_id) setStaffId(payload.staff_user_id);
      if (payload.subject_email) setSubjectEmail(payload.subject_email);
      if (payload.lar_full_legal_name) setLarName(payload.lar_full_legal_name);
      if (payload.lar_date_of_birth) setLarDob(normaliseToIso(payload.lar_date_of_birth));
      if (payload.lar_relationship)
        setLarRelationship(payload.lar_relationship as typeof larRelationship);
      if (payload.lar_relationship_other) setLarRelationshipOther(payload.lar_relationship_other);
      if (payload.parent_full_legal_name) setParentName(payload.parent_full_legal_name);
      if (payload.parent_date_of_birth) setParentDob(normaliseToIso(payload.parent_date_of_birth));
      if (payload.parent_relationship)
        setParentRelationship(payload.parent_relationship as typeof parentRelationship);
      if (payload.parent_relationship_other)
        setParentRelationshipOther(payload.parent_relationship_other);
      if (payload.process_documentation) {
        try {
          setProcessDoc({ ...EMPTY_PROCESS_DOC, ...(JSON.parse(payload.process_documentation) as ProcessDocDraft) });
        } catch {
          /* ignore a malformed draft */
        }
      }
      // Restore flow-progress state so resume shows the correct step
      if (payload.consent_record_id) {
        setGeneratedRecordId(payload.consent_record_id);
        setRemoteConsentRecordId(payload.consent_record_id);
      }
      if (payload.remote_sent) setRemoteSent(true);
    }
    // Pre-fill consent mode from prior session for re-consent (no draft needed).
    // Draft mode takes precedence (already set above via payload.mode).
    if (!payload?.mode && ctx.subject.re_consent_required && ctx.prior_consent_mode) {
      const m = ctx.prior_consent_mode;
      if (m === 'in_person_esign' || m === 'remote') setMode(m);
    }
    setHydrated(true);
  }, [ctx, hydrated]);

  // The subject PHI query in the parent wizard may resolve after this step
  // mounts, so the useState seed above can miss it. Fill the address in as
  // soon as it lands, but never clobber a draft value or something the
  // coordinator has already typed.
  useEffect(() => {
    if (!initialEmail) return;
    setSubjectEmail((cur) => (cur ? cur : initialEmail));
  }, [initialEmail]);

  // Pre-fill identity fields from the prior signed record in re-consent mode.
  // Runs after draft hydration (waits for hydrated=true) so draft values win if present.
  useEffect(() => {
    if (recordPrefilled || !hydrated || !priorRecord) return;
    setRecordPrefilled(true);
    if (!legalName && priorRecord.subject_full_legal_name)
      setLegalName(priorRecord.subject_full_legal_name);
    if (!dob && priorRecord.date_of_birth) setDob(normaliseToIso(priorRecord.date_of_birth));
    if (priorRecord.signer_role) setSignerRole(priorRecord.signer_role);
    // LAR fields — only for LAR role
    if (priorRecord.signer_role === 'lar') {
      if (!larName && priorRecord.lar_full_legal_name) setLarName(priorRecord.lar_full_legal_name);
      if (!larDob && priorRecord.lar_date_of_birth)
        setLarDob(normaliseToIso(priorRecord.lar_date_of_birth));
      if (!larRelationship && priorRecord.lar_relationship_to_subject)
        setLarRelationship(priorRecord.lar_relationship_to_subject as typeof larRelationship);
    }
    // Parent/guardian fields — only for parent or assenting_minor role
    if (priorRecord.signer_role === 'parent' || priorRecord.signer_role === 'assenting_minor') {
      if (!parentName && priorRecord.parent_full_legal_name)
        setParentName(priorRecord.parent_full_legal_name);
      if (!parentDob && priorRecord.parent_date_of_birth)
        setParentDob(normaliseToIso(priorRecord.parent_date_of_birth));
      if (!parentRelationship && priorRecord.parent_relationship)
        setParentRelationship(priorRecord.parent_relationship as typeof parentRelationship);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorRecord, hydrated, recordPrefilled]);

  // ── Mutations ────────────────────────────────────────────────────────────
  const saveDraft = useMutation({
    mutationFn: () => {
      const payload: Record<string, string | boolean> = {};
      if (legalName) payload.subject_full_legal_name = legalName;
      if (dob) payload.date_of_birth = dob;
      payload.signer_role = signerRole;
      payload.mode = mode;
      if (staffId) payload.staff_user_id = staffId;
      if (subjectEmail) payload.subject_email = subjectEmail;
      payload.process_documentation = JSON.stringify(processDoc);
      // Persist flow-progress so resume restores the correct step
      const recordId = remoteConsentRecordId ?? generatedRecordId;
      if (recordId) payload.consent_record_id = recordId;
      if (remoteSent) payload.remote_sent = true;
      if (signerRole === 'lar') {
        if (larName) payload.lar_full_legal_name = larName;
        if (larDob) payload.lar_date_of_birth = larDob;
        if (larRelationship) payload.lar_relationship = larRelationship;
        if (larRelationship === 'other' && larRelationshipOther)
          payload.lar_relationship_other = larRelationshipOther;
      }
      if (signerRole === 'parent' || signerRole === 'assenting_minor') {
        if (parentName) payload.parent_full_legal_name = parentName;
        if (parentDob) payload.parent_date_of_birth = parentDob;
        if (parentRelationship) payload.parent_relationship = parentRelationship;
        if (parentRelationship === 'other' && parentRelationshipOther)
          payload.parent_relationship_other = parentRelationshipOther;
      }
      return consent.saveDraft({ subject_id: subjectId, payload });
    },
    onSuccess: () => {
      toast.success('Draft saved — click Exit to leave and resume later.');
    },
    onError: (e) => toast.error(`Save failed: ${e.message}`),
  });

  const selectedStaff = eligibleStaff.find((a) => a.user_id === staffId);

  function buildConsentBody() {
    return {
      subject_id: subjectId,
      signer_role: signerRole,
      subject_full_legal_name: legalName,
      date_of_birth: toBackendDob(dob),
      signature_meaning: deriveMeaning(signerRole, recordType),
      record_type: recordType,
      staff_user_id: staffId,
      staff_name: selectedStaff?.full_name ?? '',
      staff_role: selectedStaff?.role ?? '',
      process_documentation: toProcessDocPayload(processDoc),
      ...(signerRole === 'lar'
        ? {
          lar_full_legal_name: larName,
          lar_date_of_birth: toBackendDob(larDob),
          lar_relationship_to_subject: (larRelationship || undefined) as
            | 'spouse'
            | 'parent'
            | 'guardian'
            | 'other'
            | undefined,
          ...(larRelationship === 'other'
            ? { lar_relationship_other: larRelationshipOther.trim() }
            : {}),
        }
        : signerRole === 'parent' || signerRole === 'assenting_minor'
          ? {
            parent_full_legal_name: parentName,
            parent_date_of_birth: toBackendDob(parentDob),
            parent_relationship: parentRelationship || undefined,
          }
          : {}),
    };
  }

  const generateDoc = useMutation({
    mutationFn: () =>
      consent.generateDocument(
        buildConsentBody() as Parameters<typeof consent.generateDocument>[0],
      ),
    onSuccess: (data) => {
      setGeneratedRecordId(data.consent_record_id);
      setDownloadUrl(data.download_url);
      // Punch-list f21 — upload-only: don't force-open a blank-copy download.
      // The signer already has a paper ICF in most in-person workflows; the
      // link below stays available for the rare case they need a fresh copy
      // to print, but it's no longer the primary/gating step.
      toast.success('Ready — upload the signed copy once the subject has signed on paper.');
      // Auto-save draft with all current form fields so they pre-populate on resume.
      const draftPayload: Record<string, string | boolean> = {};
      if (legalName) draftPayload.subject_full_legal_name = legalName;
      if (dob) draftPayload.date_of_birth = dob;
      draftPayload.signer_role = signerRole;
      draftPayload.mode = mode;
      if (staffId) draftPayload.staff_user_id = staffId;
      draftPayload.process_documentation = JSON.stringify(processDoc);
      draftPayload.consent_record_id = data.consent_record_id;
      if (signerRole === 'lar') {
        if (larName) draftPayload.lar_full_legal_name = larName;
        if (larDob) draftPayload.lar_date_of_birth = larDob;
        if (larRelationship) draftPayload.lar_relationship = larRelationship;
        if (larRelationship === 'other' && larRelationshipOther)
          draftPayload.lar_relationship_other = larRelationshipOther;
      }
      if (signerRole === 'parent' || signerRole === 'assenting_minor') {
        if (parentName) draftPayload.parent_full_legal_name = parentName;
        if (parentDob) draftPayload.parent_date_of_birth = parentDob;
        if (parentRelationship) draftPayload.parent_relationship = parentRelationship;
        if (parentRelationship === 'other' && parentRelationshipOther)
          draftPayload.parent_relationship_other = parentRelationshipOther;
      }
      consent.saveDraft({ subject_id: subjectId, payload: draftPayload }).catch(() => {
        /* non-fatal */
      });
    },
    onError: (e) => toast.error(`Could not generate: ${e.message}`),
  });

  const confirmUpload = useMutation({
    mutationFn: async () => {
      if (!signedFile || !generatedRecordId) throw new Error('No file selected');
      const { upload_url, s3_key } = await consent.presignSignedUpload({
        consent_record_id: generatedRecordId,
      });
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        body: signedFile,
        headers: {
          'Content-Type': 'application/pdf',
          'x-amz-server-side-encryption': 'AES256',
        },
      });
      if (!putRes.ok) throw new Error(`Upload to S3 failed: ${putRes.status}`);
      await consent.confirmSignedUpload({
        consent_record_id: generatedRecordId,
        s3_key,
        ...(signedVersionId ? { consent_document_id: signedVersionId } : {}),
      });
    },
    onSuccess: () => {
      setUploadConfirmed(true);
      toast.success('Signed document uploaded. Consent complete.');
      onComplete();
    },
    onError: (e) => toast.error(`Upload failed: ${e.message}`),
  });

  const startSession = useMutation({
    mutationFn: async () => {
      const body = buildConsentBody();
      const data = await consent.startSession({
        subject_id: body.subject_id,
        signer_email: subjectEmail.toLowerCase(),
        subject_full_legal_name: body.subject_full_legal_name,
        date_of_birth: body.date_of_birth,
        signature_meaning: body.signature_meaning,
        record_type: body.record_type,
        signer_role: body.signer_role,
        staff_email: selectedStaff?.email,
        staff_name: selectedStaff?.full_name,
        staff_role: selectedStaff?.role,
        lar_full_legal_name: (body as { lar_full_legal_name?: string }).lar_full_legal_name,
        lar_date_of_birth: (body as { lar_date_of_birth?: string }).lar_date_of_birth,
        lar_relationship_to_subject: (body as { lar_relationship_to_subject?: string })
          .lar_relationship_to_subject as 'spouse' | 'parent' | 'guardian' | 'other' | undefined,
        parent_full_legal_name: (body as { parent_full_legal_name?: string }).parent_full_legal_name,
        parent_date_of_birth: (body as { parent_date_of_birth?: string }).parent_date_of_birth,
        parent_relationship: (body as { parent_relationship?: string }).parent_relationship,
        consent_mode: mode === 'in_person_esign' ? 'in_person_esign' : 'remote',
        staff_user_id: staffId || undefined,
        process_documentation: body.process_documentation,
      });
      await consent.sendLink(data.consent_record_id, {
        consent_mode: mode === 'in_person_esign' ? 'in_person_esign' : 'remote',
      });
      return data;
    },
    onSuccess: (data) => {
      setRemoteSent(true);
      setRemoteConsentRecordId(data.consent_record_id);
      staffEmailTriggeredRef.current = false;
      finalizeTriggeredRef.current = false;
      localStorage.removeItem(`consent_staff_pin_sent_${data.consent_record_id}`);
      if (mode === 'in_person_esign') {
        window.open(
          `${window.location.origin}/consent/remote?cr=${data.consent_record_id}`,
          '_blank',
          'noopener,noreferrer',
        );
        toast.success('In-person signing form opened in new tab.');
      } else {
        toast.success('Signing link sent to the subject. They\'ll request their PIN from the link.');
      }
      // Auto-save draft immediately so all form fields + progress state are
      // persisted before the user advances to the next wizard step.
      const draftPayload: Record<string, string | boolean> = {};
      if (legalName) draftPayload.subject_full_legal_name = legalName;
      if (dob) draftPayload.date_of_birth = dob;
      draftPayload.signer_role = signerRole;
      draftPayload.mode = mode;
      if (staffId) draftPayload.staff_user_id = staffId;
      if (subjectEmail) draftPayload.subject_email = subjectEmail.toLowerCase();
      draftPayload.process_documentation = JSON.stringify(processDoc);
      draftPayload.consent_record_id = data.consent_record_id;
      draftPayload.remote_sent = true;
      if (signerRole === 'lar') {
        if (larName) draftPayload.lar_full_legal_name = larName;
        if (larDob) draftPayload.lar_date_of_birth = larDob;
        if (larRelationship) draftPayload.lar_relationship = larRelationship;
        if (larRelationship === 'other' && larRelationshipOther)
          draftPayload.lar_relationship_other = larRelationshipOther;
      }
      if (signerRole === 'parent' || signerRole === 'assenting_minor') {
        if (parentName) draftPayload.parent_full_legal_name = parentName;
        if (parentDob) draftPayload.parent_date_of_birth = parentDob;
        if (parentRelationship) draftPayload.parent_relationship = parentRelationship;
        if (parentRelationship === 'other' && parentRelationshipOther)
          draftPayload.parent_relationship_other = parentRelationshipOther;
      }
      consent.saveDraft({ subject_id: subjectId, payload: draftPayload }).catch(() => {
        /* non-fatal */
      });
    },
    onError: (e) => toast.error(`Could not send: ${e.message}`),
  });

  const resendSubject = useMutation({
    mutationFn: async () => {
      if (!remoteConsentRecordId) throw new Error('No active consent session.');
      await consent.sendLink(remoteConsentRecordId, {
        consent_mode: mode === 'in_person_esign' ? 'in_person_esign' : 'remote',
      });
    },
    onSuccess: () =>
      toast.success(
        mode === 'in_person_esign'
          ? 'Signing form reopened.'
          : 'Signing link resent. The subject will request a fresh PIN from the link.',
      ),
    onError: (e) => toast.error(`Could not resend to subject: ${e.message}`),
  });

  const resendStaff = useMutation({
    mutationFn: async () => {
      if (!remoteConsentRecordId) throw new Error('No active consent session.');
      return consent.resendStaffEmail(remoteConsentRecordId);
    },
    onSuccess: (data) =>
      toast.success(
        mode === 'in_person_esign'
          ? `PIN sent to ${data.staff_name}.`
          : `PIN + co-sign link sent to ${data.staff_name}.`,
      ),
    onError: (e) => toast.error(`Could not resend to staff: ${e.message}`),
  });

  const finalize = useMutation({
    mutationFn: () => consent.finalizeConsent(remoteConsentRecordId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consent', 'records', subjectId] });
      qc.invalidateQueries({ queryKey: ['subjects'] });
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
    },
    onError: () => {
      /* non-fatal — step 5 and enroll handler are fallbacks */
    },
  });

  // ── Validation ───────────────────────────────────────────────────────────
  function validate(): boolean {
    const next: typeof errors = {};
    const processNext = validateProcessDoc(processDoc);
    setProcessErrors(processNext);
    if (Object.keys(processNext).length > 0) {
      toast.error('Complete the consent process documentation before starting.');
    }
    if (legalName.trim().length < 2) next.legal_name = 'Full legal name is required.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      next.dob = 'Date of birth is required.';
    } else if (signerRole === 'subject') {
      const parts = dob.split('-').map(Number);
      const age18 = new Date((parts[0] ?? 0) + 18, (parts[1] ?? 1) - 1, parts[2] ?? 1);
      if (new Date() < age18) {
        next.dob =
          'Subject is under 18 — parental or guardian consent is required. Please change the Signer Role to "Parent / Guardian" or "Assenting Minor" and complete the parent / guardian details.';
      }
    }
    if (!staffId) next.staff = 'Please select a staff co-signer.';
    if (
      (mode === 'remote' || mode === 'in_person_esign') &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subjectEmail)
    ) {
      next.email =
        mode === 'in_person_esign'
          ? 'Subject email is required to deliver the signing PIN.'
          : 'Subject email is required for remote consent.';
    }
    if (signerRole === 'lar') {
      if (larName.trim().length < 2) next.lar_name = "LAR's full legal name is required.";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(larDob)) {
        next.lar_dob = 'LAR date of birth is required.';
      } else {
        const [y, m, d] = larDob.split('-').map(Number);
        const age18 = new Date((y ?? 0) + 18, (m ?? 1) - 1, d ?? 1);
        if (new Date() < age18) next.lar_dob = 'LAR must be at least 18 years old.';
      }
      if (!larRelationship) next.lar_relationship = 'Relationship to subject is required.';
      if (larRelationship === 'other' && !larRelationshipOther.trim())
        next.lar_relationship_other = 'Please specify the relationship.';
    }
    if (signerRole === 'parent' || signerRole === 'assenting_minor') {
      if (parentName.trim().length < 2)
        next.parent_name = "Parent/guardian's full legal name is required.";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(parentDob)) {
        next.parent_dob = "Parent/guardian's date of birth is required.";
      } else {
        const [y, m, d] = parentDob.split('-').map(Number);
        const age18 = new Date((y ?? 0) + 18, (m ?? 1) - 1, d ?? 1);
        if (new Date() < age18) next.parent_dob = 'Parent/guardian must be at least 18 years old.';
      }
      if (!parentRelationship) next.parent_relationship = 'Relationship to minor is required.';
      if (parentRelationship === 'other' && !parentRelationshipOther.trim())
        next.parent_relationship_other = 'Please specify the relationship.';
    }
    setErrors(next);
    return Object.keys(next).length === 0 && Object.keys(processNext).length === 0;
  }

  // DocuSeal postMessage (remote in-browser signing fallback)
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (typeof e.data !== 'object' || !e.data) return;
      if ((e.data as { type?: string }).type === 'docuseal.form.completed') {
        toast.success('Signature recorded.');
        onComplete();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onComplete]);

  // ── Footer trigger wiring ────────────────────────────────────────────────
  const isFormDone = generatedRecordId !== null || remoteSent;

  useEffect(() => {
    if (saveTrigger) saveTrigger.current = () => saveDraft.mutate();

    // Consent already signed — just advance
    if (activeConsentRecordId) {
      if (triggerRef) triggerRef.current = onComplete;
      onLabelChange?.('Continue to next step');
      onNextDisabledChange?.(false);
      return;
    }

    // Phase 1 item 10 (AC3) — layout preflight for the two e-sign
    // methods only. /consent/sessions/start builds the DocuSeal envelope
    // from the drag-drop layout, so it refuses when there is none; block
    // the CTA client-side with the fix path (open the layout editor).
    // Paper Sign (/consent/sessions/generate-document) serves the raw ICF
    // and the subject signs on the printed form — no DocuSeal, no layout.
    const noLayout = !!activeDoc && activeDoc.has_drag_drop_layout !== 'true';
    if (triggerRef) {
      if (mode === 'in_person') {
        if (!generatedRecordId) {
          triggerRef.current = () => {
            if (validate()) generateDoc.mutate();
          };
        } else {
          triggerRef.current = null;
        }
      } else {
        if (remoteSent) {
          triggerRef.current = allPartiesSigned ? onComplete : null;
        } else {
          triggerRef.current = () => {
            if (noLayout) {
              toast.error(
                `Active consent version ${activeDoc?.version_label ?? ''} has no signature layout. Open the layout editor and place at least one Subject + Staff signature field before signing can start.`,
              );
              return;
            }
            if (validate()) startSession.mutate();
          };
        }
      }
    }

    if (mode === 'in_person') {
      if (!generatedRecordId) {
        onLabelChange?.('Start Paper Sign');
        onNextDisabledChange?.(ctxLoading || !activeDoc);
      } else {
        onLabelChange?.('');
        onNextDisabledChange?.(true);
      }
    } else {
      if (remoteSent) {
        if (allPartiesSigned) {
          onLabelChange?.('Continue to next step');
          onNextDisabledChange?.(false);
        } else if (docusealStatus?.subject_signed) {
          onLabelChange?.('Awaiting staff co-signature…');
          onNextDisabledChange?.(true);
        } else {
          onLabelChange?.('Awaiting subject signature…');
          onNextDisabledChange?.(true);
        }
      } else {
        onLabelChange?.(
          mode === 'in_person_esign' ? 'Open eSign session' : 'Send PIN & Link to Subject',
        );
        onNextDisabledChange?.(ctxLoading || !activeDoc);
      }
    }
  });

  useEffect(() => {
    onPendingChange?.(generateDoc.isPending || startSession.isPending);
  }, [generateDoc.isPending, startSession.isPending, onPendingChange]);

  useEffect(() => {
    onSavePendingChange?.(saveDraft.isPending);
  }, [saveDraft.isPending, onSavePendingChange]);

  // When the poll detects the subject has signed (docusealStatus.subject_signed),
  // automatically send the staff co-sign PIN+link email. This covers the local dev case
  // where webhooks can't reach localhost.
  // Guard on queue_item_exists: if the webhook already processed the event and
  // inserted the queue item (which also sent the staff email), skip this auto-send
  // to avoid duplicates on every page re-visit.
  // Skipped for in_person_esign — staff is on the same device and uses the
  // "Staff Sign Now" button; no email notification is sent.
  useEffect(() => {
    // Auto-send is a local-dev-only fallback for when the DocuSeal webhook
    // can't reach localhost. On staging/production the webhook handles it;
    // running this there causes unexpected emails when anyone opens Step 2.
    if (!import.meta.env.DEV) return;
    if (!remoteConsentRecordId) return;
    if (mode === 'in_person_esign') return;
    // If the backend webhook already handled it (queue item exists), don't auto-send.
    if (docusealStatus?.queue_item_exists) {
      staffEmailTriggeredRef.current = true;
      return;
    }
    const sentKey = `consent_staff_pin_sent_${remoteConsentRecordId}`;
    if (localStorage.getItem(sentKey)) {
      staffEmailTriggeredRef.current = true;
      return;
    }
    if (
      docusealStatus?.subject_signed &&
      !docusealStatus.staff_signed &&
      !subjectHasSigned &&
      !staffEmailTriggeredRef.current &&
      !resendStaff.isPending
    ) {
      staffEmailTriggeredRef.current = true;
      localStorage.setItem(sentKey, '1');
      resendStaff.mutate();
    }
  }, [docusealStatus, subjectHasSigned, remoteConsentRecordId, resendStaff, mode]);

  // When poll confirms both parties have signed in DocuSeal but DB hasn't been
  // updated (webhook didn't fire), finalize the consent record here so the subject
  // status badge updates to "Consented" without waiting for step 5.
  useEffect(() => {
    if (!remoteConsentRecordId) return;
    if (!allPartiesSigned) return;
    if (subjectHasSigned) return; // DB already up-to-date via webhook
    if (finalizeTriggeredRef.current || finalize.isPending || finalize.isSuccess) return;
    finalizeTriggeredRef.current = true;
    finalize.mutate();
  }, [allPartiesSigned, subjectHasSigned, remoteConsentRecordId, finalize]);

  // ── Derive progress stages ───────────────────────────────────────────────
  const identifyState: StageState = isFormDone ? 'completed' : 'active';
  const signState: StageState = uploadConfirmed
    ? 'completed'
    : generatedRecordId || remoteSent
      ? 'active'
      : 'pending';
  const confirmedState: StageState = uploadConfirmed ? 'completed' : 'pending';
  const stages: Record<SigningStage, StageState> = {
    identify: identifyState,
    sign: signState,
    confirmed: confirmedState,
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (ctxLoading || !hydrated) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading consent…
      </div>
    );
  }

  // ── Remote consent fully signed via webhook (signed_at set in DB) ────────
  // Show summary card without needing activeConsentRecordId to propagate back
  // through the stale wizard cache. Prevents the "empty form" on re-visit.
  if (remoteSent && subjectHasSigned && remoteRecord) {
    const signedAt = remoteRecord.signed_at ? fmtDateTime(remoteRecord.signed_at) : null;
    return (
      <div className="space-y-6">
        <h2 className="text-base font-semibold text-ink-primary">Informed Consent</h2>
        <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Consent signed
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-emerald-800">
            <div>
              <dt className="font-medium">ICF version</dt>
              <dd className="mt-0.5 text-emerald-700">{activeDoc?.version_label ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-medium">Consent method</dt>
              <dd className="mt-0.5 text-emerald-700">Remote (DocuSeal)</dd>
            </div>
            <div>
              <dt className="font-medium">Subject / signer</dt>
              <dd className="mt-0.5 text-emerald-700">{remoteRecord.subject_full_legal_name}</dd>
            </div>
            {signedAt && (
              <div className="col-span-2">
                <dt className="font-medium">Signed</dt>
                <dd className="mt-0.5 text-emerald-700">{signedAt}</dd>
              </div>
            )}
          </dl>
        </div>
        <ConsentDocumentCard doc={activeDoc} />
      </div>
    );
  }

  // ── Consent already signed — read-only summary ───────────────────────────
  if (activeConsentRecordId) {
    // `docuseal_submission_id === 'in_person'` is the paper sentinel; anything
    // else is an electronic session (DocuSeal). Distinguishing In-Person e-Sign
    // from Remote e-Sign at read-back requires the consent_mode column on the
    // record — deferred to the subject email/mobile schema pass.
    const consentMethod =
      signedRecord?.docuseal_submission_id === 'in_person'
        ? humanConsentMode('in_person')
        : `${humanConsentMode('esign')} (DocuSeal)`;
    const signedAt = signedRecord?.signed_at ? fmtDateTime(signedRecord.signed_at) : null;
    const signerRoleLabel: Record<string, string> = {
      subject: 'Subject',
      parent: 'Parent / Guardian',
      assenting_minor: 'Assenting Minor',
      lar: 'Legally Authorised Representative',
    };

    return (
      <div className="space-y-6">
        <h2 className="text-base font-semibold text-ink-primary">Informed Consent</h2>

        <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Consent signed
          </div>
          {recordsQ.isLoading ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading record…
            </div>
          ) : signedRecord ? (
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-emerald-800">
              <div>
                <dt className="font-medium">ICF version</dt>
                <dd className="mt-0.5 text-emerald-700">{activeDoc?.version_label ?? '—'}</dd>
              </div>
              <div>
                <dt className="font-medium">Consent method</dt>
                <dd className="mt-0.5 text-emerald-700">{consentMethod}</dd>
              </div>
              <div>
                <dt className="font-medium">Subject / signer</dt>
                <dd className="mt-0.5 text-emerald-700">{signedRecord.subject_full_legal_name}</dd>
              </div>
              <div>
                <dt className="font-medium">Signer role</dt>
                <dd className="mt-0.5 text-emerald-700">
                  {signerRoleLabel[signedRecord.signer_role ?? 'subject'] ??
                    signedRecord.signer_role}
                </dd>
              </div>
              {signedAt && (
                <div className="col-span-2">
                  <dt className="font-medium">Signed</dt>
                  <dd className="mt-0.5 text-emerald-700">{signedAt}</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="mt-2 text-xs text-emerald-700">Consent record confirmed.</p>
          )}
        </div>

        <ConsentDocumentCard doc={activeDoc} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SigningProgress stages={stages} />
      <h2 className="text-base font-semibold text-ink-primary">Informed Consent</h2>

      {recordType === 're_consent' && ctx?.prior_version_label && activeDoc && (
        <ReConsentBanner
          fromVersion={ctx.prior_version_label}
          toVersion={activeDoc.version_label}
        />
      )}

      {ctx?.draft && hydrated && (
        <div className="rounded border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-800">
          Draft loaded from {fmtDateTime(ctx.draft.updated_at)}. Continue where you left off, or
          edit and save again.
        </div>
      )}

      <ConsentDocumentCard doc={activeDoc} />

      {/* ── Identity + co-signer fieldset ──────────────────────────────── */}
      <fieldset className="space-y-4" disabled={isFormDone}>
        <legend className="text-sm font-semibold text-ink-primary">Identity Verification</legend>

        {/* Signer role */}
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">Signer role</span>
          <select
            value={signerRole}
            onChange={(e) => setSignerRole(e.target.value as typeof signerRole)}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm disabled:opacity-60"
          >
            <option value="subject">Subject</option>
            <option value="parent">Parent</option>
            <option value="assenting_minor">Assenting minor</option>
            <option value="lar">Legally authorised representative (LAR)</option>
          </select>
        </label>

        {/* Parent / guardian co-consent block */}
        {(signerRole === 'parent' || signerRole === 'assenting_minor') && (
          <div className="space-y-3 rounded border border-blue-200 bg-blue-50 px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-800">
              {signerRole === 'assenting_minor'
                ? 'Parent / Guardian co-consent (required)'
                : 'Parent / Guardian details'}
            </div>
            {signerRole === 'assenting_minor' && (
              <p className="text-[11px] text-blue-700">
                Per ICH-GCP E6(R2) §4.8.15, a capable minor's assent must be accompanied by parental
                consent.
              </p>
            )}
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Parent/guardian full legal name <span className="text-red-600">*</span>
              </span>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              />
              {errors.parent_name && (
                <div className="mt-1 text-[11px] text-red-600">{errors.parent_name}</div>
              )}
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Parent/guardian date of birth <span className="text-red-600">*</span>
              </span>
              <input
                type="date"
                value={parentDob}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setParentDob(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              />
              {errors.parent_dob && (
                <div className="mt-1 text-[11px] text-red-600">{errors.parent_dob}</div>
              )}
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Relationship to minor <span className="text-red-600">*</span>
              </span>
              <select
                value={parentRelationship}
                onChange={(e) => setParentRelationship(e.target.value as typeof parentRelationship)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                <option value="father">Father</option>
                <option value="mother">Mother</option>
                <option value="guardian">Legal guardian</option>
                <option value="other">Other</option>
              </select>
              {errors.parent_relationship && (
                <div className="mt-1 text-[11px] text-red-600">{errors.parent_relationship}</div>
              )}
            </label>
            {parentRelationship === 'other' && (
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">
                  Specify relationship <span className="text-red-600">*</span>
                </span>
                <input
                  type="text"
                  value={parentRelationshipOther}
                  onChange={(e) => setParentRelationshipOther(e.target.value)}
                  placeholder="e.g. Step-parent, grandparent…"
                  maxLength={100}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
                />
                {errors.parent_relationship_other && (
                  <div className="mt-1 text-[11px] text-red-600">
                    {errors.parent_relationship_other}
                  </div>
                )}
              </label>
            )}
          </div>
        )}

        {/* LAR details block */}
        {signerRole === 'lar' && (
          <div className="space-y-3 rounded border border-accent-200 bg-accent-50 px-3 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-accent-800">
              LAR details
            </div>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                LAR full legal name <span className="text-red-600">*</span>
              </span>
              <input
                type="text"
                value={larName}
                onChange={(e) => setLarName(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              />
              {errors.lar_name && (
                <div className="mt-1 text-[11px] text-red-600">{errors.lar_name}</div>
              )}
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                LAR date of birth <span className="text-red-600">*</span>
              </span>
              <input
                type="date"
                value={larDob}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setLarDob(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              />
              {errors.lar_dob && (
                <div className="mt-1 text-[11px] text-red-600">{errors.lar_dob}</div>
              )}
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Relationship to subject <span className="text-red-600">*</span>
              </span>
              <select
                value={larRelationship}
                onChange={(e) => setLarRelationship(e.target.value as typeof larRelationship)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                <option value="spouse">Spouse</option>
                <option value="parent">Parent</option>
                <option value="guardian">Guardian</option>
                <option value="other">Other</option>
              </select>
              {errors.lar_relationship && (
                <div className="mt-1 text-[11px] text-red-600">{errors.lar_relationship}</div>
              )}
            </label>
            {larRelationship === 'other' && (
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">
                  Specify relationship <span className="text-red-600">*</span>
                </span>
                <input
                  type="text"
                  value={larRelationshipOther}
                  onChange={(e) => setLarRelationshipOther(e.target.value)}
                  placeholder="e.g. Step-parent, grandparent…"
                  maxLength={100}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
                />
                {errors.lar_relationship_other && (
                  <div className="mt-1 text-[11px] text-red-600">
                    {errors.lar_relationship_other}
                  </div>
                )}
              </label>
            )}
          </div>
        )}

        {/* Subject name + DOB */}
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">
              {signerRole === 'parent'
                ? 'Minor subject full legal name'
                : 'Subject full legal name'}{' '}
              <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            {errors.legal_name && (
              <div className="mt-1 text-[11px] text-red-600">{errors.legal_name}</div>
            )}
          </label>
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">
              {signerRole === 'parent' ? 'Minor date of birth' : 'Date of birth'}{' '}
              <span className="text-red-600">*</span>
            </span>
            <input
              type="date"
              value={dob}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setDob(e.target.value)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            {errors.dob && <div className="mt-1 text-[11px] text-red-600">{errors.dob}</div>}
          </label>
        </div>

        {/* Signature meaning (read-only) */}
        <div className="block text-xs">
          <span className="font-medium text-ink-secondary">Signature meaning</span>
          <div className="mt-1 rounded border border-surface-border bg-surface-muted/40 px-3 py-2 text-sm italic text-ink-secondary">
            {deriveMeaning(signerRole, recordType)}
          </div>
          <span className="mt-1 block text-[10px] text-ink-muted">
            Auto-set based on signer role and consent type.
          </span>
        </div>

        {/* Staff co-signer */}
        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">
            Staff co-signer <span className="text-red-600">*</span>
          </span>
          {staffQ.isLoading ? (
            <div className="mt-1 flex items-center gap-2 text-ink-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading staff…
            </div>
          ) : eligibleStaff.length === 0 ? (
            <div className="mt-1 text-[11px] text-amber-700">
              No eligible staff assigned to this study. Assign a PI, sub-investigator, or
              coordinator first.
            </div>
          ) : (
            <select
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="">Select staff co-signer…</option>
              {eligibleStaff.map((a) => (
                <option key={a.user_id} value={a.user_id}>
                  {a.full_name} — {a.role.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          )}
          {errors.staff && <div className="mt-1 text-[11px] text-red-600">{errors.staff}</div>}
        </label>
      </fieldset>

      {/* ── Consent process documentation (NIDCR sheet) ───────────────────── */}
      {!isFormDone && (
        <ConsentProcessDocumentationFields
          value={processDoc}
          onChange={(next) => {
            setProcessDoc(next);
            if (Object.keys(processErrors).length) setProcessErrors(validateProcessDoc(next));
          }}
          errors={processErrors}
          subjectId={subjectId}
          staffUserId={staffId}
          consentMode={mode}
        />
      )}

      {/* ── Signing channel ────────────────────────────────────────────────── */}
      <fieldset className="space-y-3">
        <legend className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
          <ShieldCheck className="h-4 w-4 text-accent-700" />
          Consent Method
        </legend>
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          21 CFR Part 11 compliant signature required.
        </div>

        {/* Mode picker — hidden once a session is underway */}
        {!isFormDone && (
          <div className="grid grid-cols-3 gap-2 text-xs">
            <label
              className={`flex cursor-pointer flex-col gap-1 rounded border px-3 py-2 ${mode === 'in_person' ? 'border-accent-500 bg-accent-50 text-accent-900' : 'border-surface-border text-ink-secondary'}`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="consent-mode"
                  value="in_person"
                  checked={mode === 'in_person'}
                  onChange={() => setMode('in_person')}
                  className="accent-accent-600"
                />
                <span className="font-medium">Paper Sign</span>
              </div>
              <span className="text-[10px] text-ink-muted">
                Subject signs a paper ICF. Coordinator records the paper consent in the system (date, version, signer).
              </span>
            </label>
            <label
              className={`flex cursor-pointer flex-col gap-1 rounded border px-3 py-2 ${mode === 'in_person_esign' ? 'border-accent-500 bg-accent-50 text-accent-900' : 'border-surface-border text-ink-secondary'}`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="consent-mode"
                  value="in_person_esign"
                  checked={mode === 'in_person_esign'}
                  onChange={() => setMode('in_person_esign')}
                  className="accent-accent-600"
                />
                <span className="font-medium">In-Person e-Sign</span>
              </div>
              <span className="text-[10px] text-ink-muted">
                Electronic consent on-site (tablet / kiosk). System captures the electronic signature.
              </span>
            </label>
            <label
              className={`flex cursor-pointer flex-col gap-1 rounded border px-3 py-2 ${mode === 'remote' ? 'border-accent-500 bg-accent-50 text-accent-900' : 'border-surface-border text-ink-secondary'}`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="consent-mode"
                  value="remote"
                  checked={mode === 'remote'}
                  onChange={() => setMode('remote')}
                  className="accent-accent-600"
                />
                <span className="font-medium">Remote e-Sign</span>
              </div>
              <span className="text-[10px] text-ink-muted">
                Electronic consent off-site. Secure link + OTP delivered to the subject.
              </span>
            </label>
          </div>
        )}

        {/* Remote / In Person (eSign): email input */}
        {(mode === 'remote' || mode === 'in_person_esign') && !remoteSent && (
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">
              Subject email <span className="text-red-600">*</span>
            </span>
            <input
              type="email"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              placeholder="subject@example.com"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            {errors.email && <div className="mt-1 text-[11px] text-red-600">{errors.email}</div>}
            <span className="mt-1 block text-[10px] text-ink-muted">
              {mode === 'in_person_esign'
                ? `A 4-digit PIN will be emailed to ${subjectEmail || 'the subject'}. The signing form opens in a new tab — the subject enters the PIN to authenticate and sign. Staff (${selectedStaff?.full_name ?? 'selected co-signer'}) will co-sign on the same device after.`
                : `Two emails sent: one with a 4-digit PIN, one with the document link. Subject enters both to access and sign. Staff (${selectedStaff?.full_name ?? 'selected staff'}) will be notified to co-sign after the subject.`}
            </span>
          </label>
        )}

        {/* Remote: sent confirmation */}
        {remoteSent && submissionStatusQ.isLoading && !allPartiesSigned && (
          <div className="flex items-center gap-2 rounded border border-surface-border px-4 py-3 text-xs text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" />
            Checking signing status…
          </div>
        )}
        {remoteSent && !submissionStatusQ.isLoading && (
          <div
            className={`rounded border px-4 py-3 text-xs ${allPartiesSigned ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : docusealStatus?.subject_signed ? 'border-blue-200 bg-blue-50 text-blue-900' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
          >
            <div className="font-semibold">
              {allPartiesSigned
                ? 'All parties have signed.'
                : docusealStatus?.subject_signed
                  ? mode === 'in_person_esign'
                    ? 'Subject signed — ready for staff co-signature.'
                    : 'Subject signed — co-sign notification sent to staff.'
                  : mode === 'in_person_esign'
                    ? 'Signing form opened — awaiting subject signature.'
                    : 'PIN & link sent — awaiting subject signature.'}
            </div>
            <p className="mt-1">
              {allPartiesSigned ? (
                <>
                  Both the subject and{' '}
                  <span className="font-medium">{selectedStaff?.full_name}</span> have signed.
                  Consent is complete.
                </>
              ) : docusealStatus?.subject_signed ? (
                mode === 'in_person_esign' ? (
                  <>
                    The subject has signed. Click{' '}
                    <span className="font-medium">Staff Sign Now</span> to open the co-sign form for{' '}
                    <span className="font-medium">{selectedStaff?.full_name}</span> on this device.
                  </>
                ) : (
                  <>
                    The subject has signed. A co-sign notification was sent to{' '}
                    <span className="font-medium">{selectedStaff?.full_name}</span>.
                  </>
                )
              ) : mode === 'in_person_esign' ? (
                <>
                  The signing form was opened in a new tab. The subject should enter the PIN from
                  their email and complete signing.
                </>
              ) : (
                <>
                  Two emails sent to <span className="font-medium">{subjectEmail}</span>. Waiting
                  for the subject to sign;{' '}
                  <span className="font-medium">{selectedStaff?.full_name}</span> will be notified
                  to co-sign once the subject has signed. The PIN expires in 24 hours.
                </>
              )}
            </p>
            {/* Action buttons — conditional on who still needs to act */}
            {!allPartiesSigned && !docusealStatus?.subject_signed && (
              <div className="mt-3 flex flex-wrap gap-2">
                {mode === 'in_person_esign' && remoteConsentRecordId && (
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `${window.location.origin}/consent/remote?cr=${remoteConsentRecordId}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                    className="rounded border border-amber-400 bg-amber-100 px-3 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-200"
                  >
                    Re-open signing form
                  </button>
                )}
                {/* In-person: the subject requests their PIN on the signing form itself. */}
                {mode !== 'in_person_esign' && (
                  <button
                    type="button"
                    disabled={resendSubject.isPending}
                    onClick={() => resendSubject.mutate()}
                    className="rounded border border-amber-400 bg-amber-100 px-3 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-200 disabled:opacity-50"
                  >
                    {resendSubject.isPending ? 'Sending…' : 'Send PIN & link to Subject'}
                  </button>
                )}
              </div>
            )}
            {!allPartiesSigned &&
              docusealStatus?.subject_signed &&
              !docusealStatus?.staff_signed && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {mode === 'in_person_esign' && remoteConsentRecordId && (
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `/consent/staff-sign?cr=${encodeURIComponent(remoteConsentRecordId)}`,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                      className="rounded bg-accent-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-accent-700"
                    >
                      Staff Sign Now
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={resendStaff.isPending}
                    onClick={() => resendStaff.mutate()}
                    className="rounded border border-blue-400 bg-blue-100 px-3 py-1 text-[11px] font-medium text-blue-900 hover:bg-blue-200 disabled:opacity-50"
                  >
                    {resendStaff.isPending
                      ? 'Sending…'
                      : mode === 'in_person_esign'
                        ? 'Send PIN to Staff'
                        : 'Resend PIN & co-sign link to Staff'}
                  </button>
                </div>
              )}
          </div>
        )}

        {/* In-person: document generated → upload section (always visible once generated) */}
        {mode === 'in_person' && generatedRecordId && (
          <div
            className={`space-y-3 rounded border p-4 ${uploadConfirmed ? 'border-emerald-300 bg-emerald-50' : 'border-surface-border bg-surface-card'}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-ink-primary">
                <Upload className="h-4 w-4 text-accent-600" />
                Paper sign — upload the signed copy
                {uploadConfirmed && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Signed copy uploaded
                  </span>
                )}
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded border border-surface-border px-3 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted/50"
                  title="Only needed if you don't already have a printed copy of the ICF"
                >
                  <Download className="h-3 w-3" />
                  Need a blank copy? Download PDF
                </a>
              )}
            </div>
            <p className="text-xs text-ink-muted">
              Once the subject has signed their section and the staff co-signer has signed the
              staff section on paper, scan the fully-signed copy and upload it below to complete
              consent.
            </p>
            <div className="border-t border-surface-border pt-3">
              <div className="flex items-center gap-2 text-sm font-medium text-ink-primary">
                <Upload className="h-4 w-4 text-accent-600" />
                {uploadConfirmed ? 'Replace signed consent PDF' : 'Upload signed consent PDF'}
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                {uploadConfirmed
                  ? 'Select a different file below if the wrong document was uploaded.'
                  : 'Upload the scanned signed copy to complete consent.'}
              </p>
              {/* Which ICF version was actually signed on paper (Feedback
                  eConsent item 2). Only approved versions are offered — an
                  unapproved one was never valid to consent against, and the
                  BE refuses it. */}
              <div className="mt-3">
                <label
                  htmlFor="signed-consent-version"
                  className="block text-xs font-medium text-ink-secondary"
                >
                  Consent version signed
                </label>
                <select
                  id="signed-consent-version"
                  value={signedVersionId}
                  disabled={uploadConfirmed || confirmUpload.isPending}
                  onChange={(e) => setSignedVersionId(e.target.value)}
                  className="mt-1 w-full max-w-xs rounded border border-surface-border px-2 py-1.5 text-xs disabled:opacity-60"
                >
                  <option value="">
                    Active version{activeDoc ? ` · ${activeDoc.version_label}` : ''}
                  </option>
                  {approvedConsentVersions
                    .filter((d) => d.consent_document_id !== activeDoc?.consent_document_id)
                    .map((d) => (
                      <option key={d.consent_document_id} value={d.consent_document_id}>
                        {d.version_label}
                        {d.version_date ? ` · ${fmtDate(d.version_date)}` : ''}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-ink-muted">
                  Change this only if the subject signed an earlier approved version on paper.
                </p>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    setSignedFile(e.target.files?.[0] ?? null);
                    if (uploadConfirmed) setUploadConfirmed(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
                >
                  {signedFile ? signedFile.name : 'Choose PDF…'}
                </button>
                {signedFile && !uploadConfirmed && (
                  <button
                    type="button"
                    disabled={confirmUpload.isPending}
                    onClick={() => confirmUpload.mutate()}
                    className="rounded bg-accent-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-60"
                  >
                    {confirmUpload.isPending ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                      </span>
                    ) : (
                      'Upload Signed Consent'
                    )}
                  </button>
                )}
                {uploadConfirmed && (
                  <span className="text-xs text-ink-muted italic">
                    Click &quot;Next&quot; to continue, or choose a different file to replace.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </fieldset>
    </div>
  );
}

function ReConsentBanner({ fromVersion, toVersion }: { fromVersion: string; toVersion: string }) {
  return (
    <div className="flex items-start gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
      <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-700" />
      <div>
        <div className="font-semibold text-amber-900">Re-consent on amendment</div>
        <p className="mt-1 text-xs text-amber-800">
          A new protocol version has been activated. You previously consented to
          <span className="mx-1 font-medium">{fromVersion}</span>; please review and sign the
          updated document <span className="font-medium">{toVersion}</span>.
        </p>
      </div>
    </div>
  );
}

function ConsentDocumentCard({ doc }: { doc?: ConsentDocumentRow | null }) {
  const [opening, setOpening] = useState(false);
  if (!doc) {
    return (
      <div className="rounded border border-dashed border-surface-border bg-surface-muted/40 px-4 py-6 text-center text-xs text-ink-muted">
        No active consent document on file for this study. A coordinator must upload one before
        signing can start.
      </div>
    );
  }
  async function openDocument() {
    if (!doc) return;
    setOpening(true);
    try {
      const r = await consent.documentDownloadUrl(doc.consent_document_id);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(`Could not open document: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOpening(false);
    }
  }
  return (
    <div className="rounded border border-surface-border bg-surface-card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-muted">
            Informed Consent · {doc.version_label}
          </div>
          <div className="mt-1 text-sm font-medium text-ink-primary">{doc.file_name}</div>
          <div className="text-[11px] text-ink-muted">{Math.round(doc.file_size / 1024)} KB</div>
        </div>
        <button
          type="button"
          onClick={openDocument}
          disabled={opening}
          className="rounded border border-surface-border px-3 py-1 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
        >
          {opening ? 'Opening…' : 'View ICF'}
        </button>
      </div>
    </div>
  );
}
